import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import styles from './AIBannerSuggestionsPage.module.css';

const AIBannerSuggestionsPage = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [comment, setComment] = useState('');
  const [actionType, setActionType] = useState(''); // 'approve' or 'disapprove'
  const [filter, setFilter] = useState('all');

  const { token } = useSelector((state) => state.user);

  // Mock data for AI-suggested banners
  const mockBanners = [
    {
      id: 1,
      title: 'Summer Sale 2025',
      category: 'seasonal',
      season: 'Summer',
      image_url: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800&h=400&fit=crop',
      description: 'Bright summer colors with beach theme, perfect for summer promotions',
      suggested_text: 'Summer Sale - Up to 50% Off!',
      status: 'pending',
      created_at: '2025-06-20T10:30:00Z',
      ai_confidence: 92,
      tags: ['summer', 'sale', 'bright', 'beach'],
      comments: []
    },
    {
      id: 2,
      title: 'Black Friday Mega Sale',
      category: 'promotional',
      season: 'Fall',
      image_url: 'https://images.unsplash.com/photo-1607083206968-13611e3d76db?w=800&h=400&fit=crop',
      description: 'Dark, bold design with gold accents for Black Friday promotions',
      suggested_text: 'BLACK FRIDAY - 70% OFF EVERYTHING!',
      status: 'approved',
      created_at: '2025-06-18T14:20:00Z',
      ai_confidence: 98,
      tags: ['black friday', 'sale', 'dark', 'gold'],
      comments: [
        {
          id: 1,
          action: 'approved',
          comment: 'Perfect for our Black Friday campaign!',
          created_at: '2025-06-19T09:15:00Z'
        }
      ]
    },
    {
      id: 3,
      title: 'New Year Fresh Start',
      category: 'seasonal',
      season: 'Winter',
      image_url: 'https://images.unsplash.com/photo-1467810563316-b5476525c0f9?w=800&h=400&fit=crop',
      description: 'Clean, minimalist design with fresh colors for New Year campaigns',
      suggested_text: 'New Year, New You - Start Fresh!',
      status: 'disapproved',
      created_at: '2025-06-15T16:45:00Z',
      ai_confidence: 85,
      tags: ['new year', 'fresh', 'minimalist', 'clean'],
      comments: [
        {
          id: 1,
          action: 'disapproved',
          comment: 'The colors don\'t match our brand palette',
          created_at: '2025-06-16T11:30:00Z'
        }
      ]
    },
    {
      id: 4,
      title: 'Spring Collection Launch',
      category: 'product_launch',
      season: 'Spring',
      image_url: 'https://images.unsplash.com/photo-1441984904996-e0b6ba687e04?w=800&h=400&fit=crop',
      description: 'Vibrant spring colors with floral elements for new collection announcement',
      suggested_text: 'Spring Collection 2025 - Now Available!',
      status: 'pending',
      created_at: '2025-06-22T08:10:00Z',
      ai_confidence: 89,
      tags: ['spring', 'collection', 'floral', 'vibrant'],
      comments: []
    },
    {
      id: 5,
      title: 'Mother\'s Day Special',
      category: 'holiday',
      season: 'Spring',
      image_url: 'https://images.unsplash.com/photo-1522673607200-164d1b6ce486?w=800&h=400&fit=crop',
      description: 'Soft, elegant design with pastel colors for Mother\'s Day promotions',
      suggested_text: 'Celebrate Mom - Special Offers Inside!',
      status: 'pending',
      created_at: '2025-06-21T12:00:00Z',
      ai_confidence: 94,
      tags: ['mothers day', 'elegant', 'pastel', 'soft'],
      comments: []
    },
    {
      id: 6,
      title: 'Back to School Campaign',
      category: 'seasonal',
      season: 'Fall',
      image_url: 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=800&h=400&fit=crop',
      description: 'Educational theme with school supplies and vibrant colors',
      suggested_text: 'Back to School - Get Ready to Learn!',
      status: 'approved',
      created_at: '2025-06-17T09:30:00Z',
      ai_confidence: 91,
      tags: ['back to school', 'education', 'supplies', 'learning'],
      comments: [
        {
          id: 1,
          action: 'approved',
          comment: 'Great for our back-to-school promotion!',
          created_at: '2025-06-18T14:20:00Z'
        }
      ]
    }
  ];

  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      setBanners(mockBanners);
      setLoading(false);
    }, 1000);
  }, []);

  const filteredBanners = banners.filter(banner => {
    if (filter === 'all') return true;
    return banner.status === filter;
  });

  const handleAction = (banner, action) => {
    setSelectedBanner(banner);
    setActionType(action);
    setShowCommentModal(true);
  };

  const submitAction = async () => {
    if (!selectedBanner) return;

    // Create new comment
    const newComment = {
      id: Date.now(),
      action: actionType,
      comment: comment,
      created_at: new Date().toISOString()
    };

    // Update banner status and comments
    setBanners(prev => prev.map(banner => 
      banner.id === selectedBanner.id 
        ? {
            ...banner,
            status: actionType === 'approve' ? 'approved' : 'disapproved',
            comments: [...banner.comments, newComment]
          }
        : banner
    ));

    // Reset modal state
    setShowCommentModal(false);
    setComment('');
    setSelectedBanner(null);
    setActionType('');
  };

  const downloadBanner = async (banner) => {
    try {
      const response = await fetch(banner.image_url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${banner.title.replace(/\s+/g, '_')}_banner.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading banner:', error);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: '#f59e0b', bg: '#fef3c7', text: 'Pending Review' },
      approved: { color: '#10b981', bg: '#d1fae5', text: 'Approved' },
      disapproved: { color: '#ef4444', bg: '#fee2e2', text: 'Disapproved' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    
    return (
      <span style={{
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '600',
        color: config.color,
        backgroundColor: config.bg,
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
      }}>
        {config.text}
      </span>
    );
  };

  const getCategoryIcon = (category) => {
    const icons = {
      seasonal: '🌟',
      promotional: '🎯',
      product_launch: '🚀',
      holiday: '🎉',
      sales: '💰'
    };
    return icons[category] || '🎨';
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p>Loading AI Banner Suggestions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <p className={styles.subtitle}>
            Discover AI-generated banner designs tailored for your brand's seasonal campaigns, promotions, and special events
          </p>
        </div>
        
        <div className={styles.filterSection}>
          <div className={styles.filterButtons}>
            {['all', 'pending', 'approved', 'disapproved'].map(filterType => (
              <button
                key={filterType}
                onClick={() => setFilter(filterType)}
                className={`${styles.filterButton} ${filter === filterType ? styles.active : ''}`}
              >
                {filterType.charAt(0).toUpperCase() + filterType.slice(1)}
                <span className={styles.count}>
                  {filterType === 'all' ? banners.length : banners.filter(b => b.status === filterType).length}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className={styles.bannersGrid}>
        {filteredBanners.map(banner => (
          <div key={banner.id} className={styles.bannerCard}>
            <div className={styles.bannerImageContainer}>
              <img 
                src={banner.image_url} 
                alt={banner.title}
                className={styles.bannerImage}
              />
              <div className={styles.bannerOverlay}>
                <div className={styles.suggestedText}>
                  "{banner.suggested_text}"
                </div>
              </div>
              <div className={styles.confidenceScore}>
                <span className={styles.aiIcon}>🤖</span>
                {banner.ai_confidence}%
              </div>
            </div>
            
            <div className={styles.bannerContent}>
              <div className={styles.bannerHeader}>
                <div className={styles.bannerTitleRow}>
                  <span className={styles.categoryIcon}>
                    {getCategoryIcon(banner.category)}
                  </span>
                  <h3 className={styles.bannerTitle}>{banner.title}</h3>
                  {getStatusBadge(banner.status)}
                </div>
                <p className={styles.bannerDescription}>{banner.description}</p>
              </div>

              <div className={styles.bannerMeta}>
                <div className={styles.tags}>
                  {banner.tags.map(tag => (
                    <span key={tag} className={styles.tag}>#{tag}</span>
                  ))}
                </div>
                <div className={styles.metaInfo}>
                  <span className={styles.season}>{banner.season}</span>
                  <span className={styles.date}>
                    {new Date(banner.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>

              {banner.comments.length > 0 && (
                <div className={styles.commentsSection}>
                  <h4 className={styles.commentsTitle}>Recent Feedback:</h4>
                  {banner.comments.slice(-1).map(comment => (
                    <div key={comment.id} className={styles.comment}>
                      <span className={`${styles.commentAction} ${styles[comment.action]}`}>
                        {comment.action === 'approved' ? '✓' : '✗'}
                      </span>
                      <span className={styles.commentText}>"{comment.comment}"</span>
                    </div>
                  ))}
                </div>
              )}

              <div className={styles.actionButtons}>
                {banner.status === 'pending' && (
                  <>
                    <button
                      onClick={() => handleAction(banner, 'disapprove')}
                      className={`${styles.actionButton} ${styles.disapprove}`}
                    >
                      <span>✗</span>
                      Disapprove
                    </button>
                    <button
                      onClick={() => handleAction(banner, 'approve')}
                      className={`${styles.actionButton} ${styles.approve}`}
                    >
                      <span>✓</span>
                      Approve
                    </button>
                  </>
                )}
                
                {banner.status === 'approved' && (
                  <button
                    onClick={() => downloadBanner(banner)}
                    className={`${styles.actionButton} ${styles.download}`}
                  >
                    <span>⬇</span>
                    Download
                  </button>
                )}
                
                {banner.status === 'disapproved' && (
                  <button
                    onClick={() => handleAction(banner, 'approve')}
                    className={`${styles.actionButton} ${styles.reconsider}`}
                  >
                    <span>🔄</span>
                    Reconsider
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredBanners.length === 0 && (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>🎨</div>
          <h3>No banners found</h3>
          <p>No AI-generated banners match your current filter criteria.</p>
        </div>
      )}

      {/* Comment Modal */}
      {showCommentModal && (
        <div className={styles.modalOverlay} onClick={() => setShowCommentModal(false)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>
                {actionType === 'approve' ? '✓ Approve' : '✗ Disapprove'} Banner
              </h3>
              <button
                onClick={() => setShowCommentModal(false)}
                className={styles.closeButton}
              >
                ×
              </button>
            </div>
            
            <div className={styles.modalContent}>
              <div className={styles.selectedBannerPreview}>
                <img 
                  src={selectedBanner?.image_url} 
                  alt={selectedBanner?.title}
                  className={styles.previewImage}
                />
                <div className={styles.previewInfo}>
                  <h4>{selectedBanner?.title}</h4>
                  <p>{selectedBanner?.description}</p>
                </div>
              </div>
              
              <div className={styles.commentSection}>
                <label className={styles.commentLabel}>
                  Add your feedback (optional):
                </label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder={`Why are you ${actionType === 'approve' ? 'approving' : 'disapproving'} this banner?`}
                  className={styles.commentInput}
                  rows={4}
                />
              </div>
            </div>
            
            <div className={styles.modalFooter}>
              <button
                onClick={() => setShowCommentModal(false)}
                className={styles.cancelButton}
              >
                Cancel
              </button>
              <button
                onClick={submitAction}
                className={`${styles.submitButton} ${styles[actionType]}`}
              >
                {actionType === 'approve' ? '✓ Approve' : '✗ Disapprove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIBannerSuggestionsPage;
